import React from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import IconPaymentMethod from 'v2/components/Shared/IconPaymentMethod';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import WhatsAppLink from 'v2/components/Shared/WhatsAppLink';
import { EPaymentMethod } from 'v2/interfaces/shopping';
import ordersService from 'v2/services/orders';

import type { PaymentMethod } from '../interfaces';

const DELIVERY_TYPE = {
  1: 'Grátis',
  2: 'Valor Fixo',
  3: 'Correios'
};

const MAIL_TYPE = {
  '04510': 'PAC',
  '04014': 'SEDEX'
};

const Customer = () => {
  const [order] = useObservable(() => ordersService.getOrder(), []);

  if (!order) {
    return null;
  }

  function getPaymentMethodName(paymentMethod: PaymentMethod) {
    if (paymentMethod.id === EPaymentMethod.PIX_AUTOMATICO) return paymentMethod.method;
    return paymentMethod.name;
  }

  return (
    <div className='text-sm font-normal'>
      {order.customer?.name && (
        <div className='mb-1'>
          <span className='font-medium'>Nome: </span>
          <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
            {order.customer.name}
          </PermissionHide>
        </div>
      )}
      {order.customer?.email && (
        <div className='mb-1'>
          <span className='font-medium'>E-mail: </span>
          <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
            {order.customer.email}
          </PermissionHide>
        </div>
      )}
      {order.customer?.celphone && (
        <div className='mb-1'>
          <span className='font-medium'>Fone: </span>
          <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
            <WhatsAppLink phone={order.customer.celphone} />
          </PermissionHide>
        </div>
      )}
      {order.payment_method?.id && (
        <div className='mb-1'>
          <span className='font-medium'>Forma de pagamento: </span>
          <IconPaymentMethod cardBrand={order.card_brand} /> {getPaymentMethodName(order.payment_method)}
          {order.card_number ? ` - ${order.card_number}` : ''}
        </div>
      )}
      {order.delivery_type?.tipofrete !== '1' && (
        <div className='mb-1'>
          <span className='font-medium'>Valor total frete: </span>
          <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
            <FormattedNumber format='BRL' value={order.freight_value} />
          </PermissionHide>
          <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
            <em>oculto</em>
          </PermissionHide>
        </div>
      )}
      {order.delivery_type && (
        <div className='mb-1'>
          <span className='font-medium'>Tipo de frete: </span>
          <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
            {order.delivery_type?.tipofrete
              ? DELIVERY_TYPE[order.delivery_type.tipofrete as unknown as keyof typeof DELIVERY_TYPE]
              : 'Desconhecido'}
            {MAIL_TYPE[order.mail_type as keyof typeof MAIL_TYPE]
              ? ` (${MAIL_TYPE[order.mail_type as keyof typeof MAIL_TYPE]})`
              : ''}
          </PermissionHide>
          <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
            <em>oculto</em>
          </PermissionHide>
        </div>
      )}
      {order.delivery_type?.prazo_frete >= 0 && (
        <div className='mb-1'>
          <span className='font-medium'>Prazo exibido ao cliente: </span>
          <PermissionHide inverse action='myeduzz:minhas-entregas:ocultar-valores'>
            {order.delivery_type.prazo_frete} dia(s)
          </PermissionHide>
          <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
            <em>oculto</em>
          </PermissionHide>
        </div>
      )}
      {order.customer?.address && (
        <div className='mb-1'>
          <span className='font-medium'>Endereço: </span>
          <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
            {order.customer.address.street && `${order.customer.address.street}, `}
            {order.customer.address.number && `${order.customer.address.number} - `}
            {order.customer.address.complement && `${order.customer.address.complement} - `}
            {order.customer.address.neighborhood && `${order.customer.address.neighborhood} - `}
            {order.customer.address.zip_code && `CEP ${order.customer.address.zip_code} - `}
            {` ${order.customer.address.city} / ${order.customer.address.state}`}
          </PermissionHide>
        </div>
      )}
    </div>
  );
};
export default Customer;
